import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, HandHeart } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

const Navbar = () => {
  const { t, lang, setLang } = useApp();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/", label: t("home") },
    { to: "/about", label: t("about") },
    { to: "/work", label: t("work") },
    { to: "/gallery", label: t("gallery") },
    { to: "/donors", label: t("donors") },
    { to: "/volunteer", label: t("volunteer") },
    { to: "/contact", label: t("contact") },
  ];

  const toggleLang = () => setLang(lang === "en" ? "hi" : "en");

  return (
    <header className="glass-header sticky top-0 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <Logo size={72} className="w-[64px] h-[64px] sm:w-[72px] sm:h-[72px] lg:w-[80px] lg:h-[80px]" />
          <div className="leading-tight">
            <div className={`font-serif-display text-[#1B4332] ${lang === "hi" ? "text-2xl sm:text-3xl lg:text-4xl font-devanagari" : "text-xl sm:text-2xl lg:text-3xl"}`}>{t("brandName")}</div>
            <div className={`text-[#4A4A4A] ${lang === "hi" ? "text-xs sm:text-sm font-devanagari" : "text-[10px] sm:text-[11px] uppercase tracking-[0.25em]"}`}>{t("brandNameSub")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#C95B24]" : "text-[#1A1A1A] hover:text-[#C95B24]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="language-toggle"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#1B4332]/15 text-sm font-medium hover:bg-[#F4EFE6] transition-colors"
            aria-label="Toggle language"
          >
            <Globe className="w-4 h-4" />
            {lang === "en" ? "हिन्दी" : "English"}
          </button>
          <Button
            onClick={() => navigate("/donate")}
            data-testid="donate-nav-btn"
            className="btn-saffron rounded-full px-5 py-2 hidden sm:inline-flex items-center gap-2"
          >
            <HandHeart className="w-4 h-4" />
            {t("donate")}
          </Button>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-md hover:bg-[#F4EFE6]"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#1B4332]/10 bg-[#FDFBF7]/95 backdrop-blur-lg" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-base ${isActive ? "text-[#C95B24] font-semibold" : "text-[#1A1A1A]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={() => { toggleLang(); setOpen(false); }}
              className="text-left py-2 text-sm text-[#4A4A4A] flex items-center gap-2"
            >
              <Globe className="w-4 h-4" /> {lang === "en" ? "हिन्दी" : "English"}
            </button>
            <Button
              onClick={() => { navigate("/donate"); setOpen(false); }}
              className="btn-saffron rounded-full w-full"
            >
              {t("donate")}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
